import { useState } from 'react'

import { Box, Button } from '@factorialco/deprecated-design-system'
import {
  PermissionsApplyRoleTemplateDocument,
  PermissionsRoleTemplatesDocument,
} from 'generated/resources/graphql'

import Modal from 'modules/core/components/Modal'
import { useCallback } from 'modules/core/hooks/useCallback'
import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useMutation } from 'modules/core/hooks/useMutation'
import { useQuery } from 'modules/core/hooks/useQuery'
import { handleResult } from 'modules/core/lib/handleMutation'
import i18n from 'modules/core/lib/i18n'
import app from 'modules/core/store/models/app'

import RoleTemplates from 'modules/permissions/components/RoleTemplates'

interface Props {
  permissionGroupId: number
  permissionGroupName: string
  onClose: () => void
}

function ApplyTemplateModal({ permissionGroupId, permissionGroupName, onClose }: Props) {
  const current = useCurrentSession()
  const { data, loading } = useQuery(PermissionsRoleTemplatesDocument, {
    variables: { companyId: current.company.id },
  })

  const [applyTemplate, { loading: applyingTemplate }] = useMutation(
    PermissionsApplyRoleTemplateDocument,
    {
      extractorFn: (result) =>
        result.permissionsMutations.applyRoleTemplatePermissionsPermissionGroup,
    }
  )

  const [selectedTemplateId, setSelectedTemplateId] = useState<string | undefined>(undefined)

  const handleSelectedTemplateChange = useCallback((id: string | undefined) => {
    setSelectedTemplateId(id)
  }, [])

  if (loading) return null

  const templates = data?.permissions.roleTemplates ?? []

  const handleSubmit = async () => {
    if (selectedTemplateId) {
      const result = await applyTemplate({
        variables: { permissionGroupId: permissionGroupId, roleTemplateId: selectedTemplateId },
      })
      const handled = handleResult(result, () => {})
      if (handled.success) {
        app.setFlash({
          type: 'correct',
          message: i18n.t('common.saved_successfully'),
        })
      } else {
        app.setFlash({
          type: 'wrong',
          message: i18n.t('common.crash.retry'),
        })
      }
    }
    onClose()
  }

  return (
    <Modal
      title={i18n.t('settings.permissions.apply_role_template.title', {
        groupName: permissionGroupName,
      })}
      onSubmit={handleSubmit}
      footer={
        <Button submit disabled={selectedTemplateId === undefined || applyingTemplate}>
          {i18n.t('settings.permissions.apply_role_template.submit')}
        </Button>
      }
    >
      <Box height="s600">
        <RoleTemplates
          templates={templates}
          onSelectedTemplateChange={handleSelectedTemplateChange}
        />
      </Box>
    </Modal>
  )
}

export default ApplyTemplateModal
